import {Injectable} from '@angular/core';
import {ConductedSurveyService} from '../../../../modules/organisation/components/organisation-survey-management/store/conducted-survey.service';
import {
  ConductedSurvey,
  ConductedSurveyDimension, ConductedSurveyQuestion, SurveySelectionType
} from '../../../../modules/organisation/components/organisation-survey-management/store/conducted-survey.model';
import {PdfUtil} from '../../../../shared/utils/pdf-util';

import * as pdfMake from 'pdfmake/build/pdfmake.js';
import * as pdfFonts from 'pdfmake/build/vfs_fonts.js';
import {PdfScorecardUtil} from '../../../../shared/utils/pdf-scorecard-util';
import {LoadingService} from '../../../../services/loading/loading.service';
import {AlertService} from '../../../../shared/modules/alert/services/alert.service';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
  providedIn: 'root'
})
export class SurveyScorecardPdfService {

  constructor(private alertService: AlertService,
              private loadingService: LoadingService,
              private conductedSurveyService: ConductedSurveyService) {
  }

  private docDefinition = PdfUtil.docDefinition();

  // private static fetchImage(uri) {
  //   return new Promise((resolve, reject) => {
  //     const image = new window.Image();
  //     image.onload = () => {
  //       const canvas = document.createElement('canvas');
  //       canvas.width = this.naturalWidth;
  //       canvas.height = this.naturalHeight;
  //       canvas.getContext('2d').drawImage(this, 0, 0);
  //       resolve(canvas.toDataURL('image/png'));
  //     };
  //     image.onerror = (params) => {
  //       reject(new Error('Cannot fetch image ' + uri + '.'));
  //     };
  //     image.src = uri;
  //   });
  // }

  private static getBase64ImageFromURL(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };
      img.onerror = error => {
        reject(error);
      };
      img.src = url;
    });
  }

  private static getQuestionColumn(row: any[], question: ConductedSurveyQuestion) {
    if (question) {
      let indicator: any = PdfUtil.getWhiteCircle();
      switch (question.answerIndicator) {
        case SurveySelectionType.RED: {
          indicator = PdfUtil.getRedCellFillColor();
          break;
        }
        case SurveySelectionType.YELLOW: {
          indicator = PdfUtil.getYellowCellFillColor();
          break;
        }
        case SurveySelectionType.GREEN: {
          indicator = PdfUtil.getGreenCellFillColor();
          break;
        }
      }


      row = row.concat([
        PdfUtil.getContentBorderedText(question.label),
        // {text: groupedQuestionSet[0].answerIndicator},
        indicator,
        PdfUtil.checkedBox(question.answerPriority)
      ]);
    } else {
      row = row.concat([{}, {}, {}]);
    }

    return row;
  }

  private static getNarrativeForPdf(content: any[], conductedSurvey: ConductedSurvey) {
    if (conductedSurvey.answerCaptureNotes) {
      content.push([
        {
          stack: [
            {
              text: 'Narrative text',
              alignment: 'center',
              fontSize: 11,
              bold: true,
              decoration: 'underline'
            },
            {
              text: conductedSurvey.answerCaptureNotes,
              fontSize: 10,
              bold: false,
              alignment: 'justify',
              margin: [30, 10, 30, 10]
            }
          ],
          // unbreakable: true,
          alignment: 'center',
          pageBreak: 'before'
        },
      ]);
    }
    return content;
  }

  private static hasIndicatorQuestions(conductedSurvey: ConductedSurvey): boolean {
    let hasIndicators = false;
    conductedSurvey?.dimensions.forEach(dimension => {
      dimension.questions.forEach(question => {
        if (question.type === 'INDICATOR') {
          hasIndicators = true;
        }
      });
    });

    return hasIndicators;
  }

  init(conductedSurveyId: any, openInSamePage = false) {
    this.fetch(conductedSurveyId, openInSamePage, undefined);
  }

  initExcludingNarrative(conductedSurveyId: any, openInSamePage = false) {
    this.fetch(conductedSurveyId, openInSamePage, true);
  }

  private fetch(conductedSurveyId: number, openInSamePage: boolean, excludeNarrative = false) {
    this.loadingService.setIsLoading(true);
    this.conductedSurveyService.findById(conductedSurveyId).subscribe(item => {
      this.buildPdf(item, openInSamePage, excludeNarrative);
      this.loadingService.setIsLoading(false);
    }, () => {
      this.alertService.setAlert('Unable to fetch survey for generating scorecard', 'error', 'client-survey-list');
      this.loadingService.setIsLoading(false);
    });
  }

  private buildPdf(conductedSurvey: ConductedSurvey, openInSamePage: boolean, excludeNarrative: boolean) {
    this.docDefinition.info = {
      title: 'Scorecard and Action Plan',
      author: 'The Greenlight Office',
      subject: 'survey scorecard pdf',
      // keywords: 'keywords for document',
    };

    this.docDefinition.header = PdfScorecardUtil.scorecardHeader(conductedSurvey);

    this.docDefinition.images = {
      checked: 'data:image/png;base64,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',
      unchecked: 'data:image/png;base64,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',
    };

    const fetches = [];

    conductedSurvey.dimensions.forEach(dimension => {
      dimension.questions.forEach(question => {
        if (question.type === 'PHOTO' && question.answerPhoto) {
          const src = question.answerPhoto.location;
          fetches.push(SurveyScorecardPdfService.getBase64ImageFromURL(src).then(data => {
            this.docDefinition.images[src] = data;
          }));
        }
      });
    });

    Promise.all(fetches).then(() => {
      this.docDefinition.content = [];
      this.docDefinition.content = this.getQuestionsForPdf(this.docDefinition.content, conductedSurvey);

      this.docDefinition.content = this.getPhotosForPdf(this.docDefinition.content, conductedSurvey);

      if (!excludeNarrative) {
        this.docDefinition.content = SurveyScorecardPdfService.getNarrativeForPdf(this.docDefinition.content, conductedSurvey);
      }


      if (openInSamePage) {
        // opens in the same tab
        pdfMake.createPdf(this.docDefinition).open({}, window);
      } else {
        // opens in a new tab
        pdfMake.createPdf(this.docDefinition).open();

        // downloads
        // pdfMake.createPdf(this.docDefinition).download('Scorecard-Report.pdf');
      }
    });
  }

  private getQuestionsForPdf(content: any[], conductedSurvey: ConductedSurvey) {
    const hasIndicators = SurveyScorecardPdfService.hasIndicatorQuestions(conductedSurvey);

    if (hasIndicators === true) {
      conductedSurvey.dimensions.forEach(dimension => {
        content.push([
          {
            stack: [this.getDimensionTable(dimension)],
            // unbreakable: true,
          },
        ]);
      });
    } else {
      content.push([
        {
          stack: [
            {
              text: 'No questions captured.',
              alignment: 'center',
              bold: true,
            },
            {
              canvas: [{type: 'line', x1: 20, y1: 10, x2: 575, y2: 10, lineWidth: 3, lineColor: '#888888'}],
              margin: [0, 0, 0, 10]
            }
          ],
          // unbreakable: true,
        },
      ]);
    }

    return content;
  }

  private getDimensionTable(dimension: ConductedSurveyDimension) {
    const secondScoreHeading = dimension.questions.length > 1 ? {text: 'Score', style: 'tableHeader'} : {};
    const secondPrioritiesHeading = dimension.questions.length > 1 ? {text: 'Priorities', style: 'tableHeader'} : {};

    const thirdScoreHeading = dimension.questions.length > 2 ? {text: 'Score', style: 'tableHeader'} : {};
    const thirdPrioritiesHeading = dimension.questions.length > 2 ? {text: 'Priorities', style: 'tableHeader'} : {};

    const groupedQuestions: ConductedSurveyQuestion[][] = dimension.questions.reduce(
      (acc, question, index) => {
        if (question.type === 'INDICATOR') {
          if (index % 3 === 0) {
            acc.push([question]);
            return acc;
          } else {
            const length = acc.length;
            acc[length - 1].push(question);
            return acc;
          }
        }
      }, []);

    if (groupedQuestions) {
      const tableData = [];
      tableData.push([
        {text: dimension.name, style: 'tableHeader', bold: true},
        {text: 'Score', style: 'tableHeader'},
        {text: 'Priorities', style: 'tableHeader'},

        {}, // space

        {},
        secondScoreHeading,
        secondPrioritiesHeading,

        {}, // space

        {},
        thirdScoreHeading,
        thirdPrioritiesHeading,
      ]);

      groupedQuestions.forEach((groupedQuestionSet) => {
        let row = [];
        row = SurveyScorecardPdfService.getQuestionColumn(row, groupedQuestionSet[0]);
        row = row.concat([{}]); // space
        row = SurveyScorecardPdfService.getQuestionColumn(row, groupedQuestionSet[1]);
        row = row.concat([{}]); // space
        row = SurveyScorecardPdfService.getQuestionColumn(row, groupedQuestionSet[2]);

        tableData.push(row);
      });

      return [
        {
          table: {
            widths: [110, 20, 20, 10, 110, 20, 20, 10, 110, 20, 20],
            headerRows: 1,
            dontBreakRows: true,
            body: tableData
          },
          layout: 'noBorders',
          margin: [20, 0, 20, 0]
        },
        {
          canvas: [{type: 'line', x1: 20, y1: 10, x2: 575, y2: 10, lineWidth: 3, lineColor: '#888888'}],
          margin: [0, 0, 0, 10]
        }
      ];
    }
  }

  private getPhotosForPdf(content: any[], conductedSurvey: ConductedSurvey) {
    const photoQuestions: ConductedSurveyQuestion[] = conductedSurvey.dimensions.reduce((accD: ConductedSurveyQuestion[], dimension) => {
      const qs = dimension.questions.reduce((accQ: ConductedSurveyQuestion[], question) => {
        if (question.type === 'PHOTO' && question.answerPhoto) {
          accQ.push(question);
        }
        return accQ;
      }, []);
      if (qs) {
        if (accD && accD.length) {
          accD.concat(qs);
        } else {
          accD = qs;
        }
      }
      return accD;
    }, []);

    if (photoQuestions.length) {
      content.push([
        {
          stack: [
            {
              text: 'Images from Greenlight capture',
              fontSize: 11,
              bold: true,
              decoration: 'underline'
            },
            this.getPhotos(photoQuestions),
          ],
          // unbreakable: true,
          alignment: 'center',
          pageBreak: 'before'
        },
      ]);
    }

    return content;
  }

  private getPhotos(photoQuestions: ConductedSurveyQuestion[]) {
    const photoSet = [];
    photoQuestions.forEach((question, ix) => {
      const pageBreak = ix === photoQuestions.length - 1 ? 'none' : 'after';
      photoSet.push([{
          stack: [
            {
              text: question.label,
              alignment: 'center',
            },
            {
              text: question.answerPhoto.firstKey,
              alignment: 'center',
            },
            {
              image: question.answerPhoto.location,
              // width: 400,
              fit: [300, 1000],
              alignment: 'center',
              pageBreak
            }
          ],
          margin: [20, 10, 10, 10],
          // unbreakable: true,
        }]
      );
    });

    return photoSet;
  }
}
